import React, { useCallback, useContext, useMemo, useRef } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import cx from 'classnames';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import shortid from 'shortid';
import { FormConstructorContext } from '@/common/containers/FormConstructor/FormConstructor';
import styles from '@/common/components/Inputs/Select/styles.less';
import { ConfirmModal } from '@/common/containers/Modals/index';
import { getArrayChange } from '@/common/utils/index';
import { useTranslation } from 'react-i18next';

const DataEntrySelect = ({ className, entryName, name, tip, required }) => {
  const { t } = useTranslation();
  const [slug, fieldKey] = name.split('.');
  const { control, getValues } = useFormContext();
  const { relativeAnswers, allAnswers } = useContext(FormConstructorContext);
  const connectedEntry = relativeAnswers[entryName];

  const initialValues = useRef(
    getValues(slug)
      ? Object.values(getValues(slug)).map((v) => v.value.firstName)
      : []
  ).current;
  const lastValue = useRef(initialValues);

  const options = useMemo(
    () =>
      !connectedEntry
        ? []
        : connectedEntry.value
        ? [
            {
              label: connectedEntry.value[fieldKey],
              value: `${connectedEntry.value[fieldKey]}||${connectedEntry.answerId}`,
            },
          ]
        : Object.values(connectedEntry).map(({ value, answerId }) => ({
            label: value[fieldKey],
            value: `${value[fieldKey]}||${answerId}`,
          })),
    [connectedEntry, fieldKey]
  );

  const formatValue = useCallback((slugValue) => {
    if (!slugValue) return [];
    return Object.values(slugValue).map((item) =>
      item ? item.value[fieldKey] : undefined
    );
  }, [fieldKey]);

  const handleChange = useCallback(
    (value) => {
      if (!value.length) return null;
      const resValue = {};
      value.forEach((answer) => {
        const uid =
          allAnswers[slug] &&
          Object.values(allAnswers[slug]).find(
            ({ value }) => value[fieldKey] === answer
          )?.uid;
        resValue[uid || shortid.generate()] = { value: { [fieldKey]: answer } };
      });
      return resValue;
    },
    [allAnswers, slug, fieldKey]
  );

  return (
    <div className={cx(styles.container, className)}>
      <br />
      <Controller
        control={control}
        name={slug}
        rules={{
          required: required ? t('Field is required') : false,
        }}
        render={({
          field: { onChange, value: fieldValue, name },
          fieldState: { error },
        }) => {
          const value = formatValue(fieldValue);
          return (
            <ConfirmModal
              confirmText={t('confirm')}
              title={t('Do you want to exclude this person?')}
              description={t('It will delete all person related tasks')}
              type="delete"
            >
              {(confirm) => (
                <>
                  <Select
                    className={styles.orphanSelect}
                    onChange={(evt, child) => {
                      const {
                        target: { value: newValue },
                      } = evt;
                      const { toDelete } = getArrayChange(
                        lastValue.current,
                        newValue
                      );
                      lastValue.current = newValue;

                      if (
                        toDelete.length &&
                        initialValues.includes(child.props.value)
                      ) {
                        confirm(() => {
                          onChange(handleChange(newValue));
                        })(evt);
                        return;
                      }

                      onChange(handleChange(newValue));
                    }}
                    value={value}
                    name={slug}
                    error={!!error}
                    multiple
                  >
                    {options.map(({ value: itemValue, label }) => (
                      <MenuItem key={itemValue} value={itemValue}>
                        <Checkbox checked={value.indexOf(itemValue) > -1} />
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                  <InputLabel
                    className={cx(
                      styles.bottomLabel,
                      !!error && styles['bottomLabel-error']
                    )}
                    htmlFor={name}
                  >
                    {error?.message || tip}
                  </InputLabel>
                </>
              )}
            </ConfirmModal>
          );
        }}
      />
      <p className={styles.orphanSelectLabel}>
        {t('To add more dependencies go to Decedent’s profile menu section')}
      </p>
    </div>
  );
};

export default DataEntrySelect;
